import { createSelector } from 'reselect';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { Dispatch } from 'redux';
import { frontloadConnect } from 'react-frontload';
import { PageIndustryAggregation } from 'components/pages/PageTaxonomyAggregation/PageIndustryAggregation';
import {
  userShortcutsUrlsSelector,
  userRolesSelector,
  isAuthSelector,
  userEmailSelector,
  userFVSSectionMemberSelector,
  userOktaIdSelector,
} from 'modules/user/selectors';
import {
  aggsPageContentSelector,
  contentLoadingSelector,
  industryContentListSelector,
  skipContentFrontloadRequestSelector,
} from 'modules/content/selectors';
import { fetchAggsPageContent, resetSkipContentFrontloadRequest } from 'modules/content/actions';
import { industryFromParamsSelectorFactory, pageIndustryAggsIndustrySlugSelector } from 'modules/router';
import { emptyObject, getPath } from 'utils';
import { Routes } from 'constants/index';
import { toggleLoginReload } from 'modules/layouts';

const mapActionCreators = (dispatch: Dispatch) => ({
  async fetchAggsPageContent(): Promise<void> {
    await dispatch(fetchAggsPageContent()).catch(() => dispatch(push(getPath(Routes.NOT_FOUND))));
  },

  toggleLoginReload(shouldReload: boolean): void {
    dispatch(toggleLoginReload(shouldReload));
  },
  resetSkipContentFrontloadRequest(): void {
    dispatch(resetSkipContentFrontloadRequest());
  },
});

const mapStateToProps = (initState: State.Root) => {
  const industryFromParamsSelector = industryFromParamsSelectorFactory(pageIndustryAggsIndustrySlugSelector);

  const userHasShortcutSelector = createSelector(
    [userShortcutsUrlsSelector, pageIndustryAggsIndustrySlugSelector],
    (userShortcutsUrls: string[], industrySlug: string): boolean => {
      if (!userShortcutsUrls || !userShortcutsUrls.length || !industrySlug) return false;
      return userShortcutsUrls.includes(`${industrySlug}`);
    }
  );

  const userRoles: string[] | undefined = userRolesSelector(initState);
  const userRolesStr: string = userRoles ? userRoles.join() : '';

  return (state: State.Root) => {
    const industrySlug: string = pageIndustryAggsIndustrySlugSelector(state);

    const industry: State.Industry | null = industryFromParamsSelector(state);
    const {
      id: industryId,
      name: industryName = '',
      description: industryDescription = '',
    }: any = industry || emptyObject;

    return {
      loading: contentLoadingSelector(state),
      industrySlug,
      industryId,
      industryName,
      industryDescription,
      aggsPageContent: aggsPageContentSelector(state),
      hasShortcut: userHasShortcutSelector(state),
      userRolesStr,
      isAuth: isAuthSelector(state),
      topicContentList: industryContentListSelector(state),
      userEmail: userEmailSelector(state),
      fvsMembership: userFVSSectionMemberSelector(state),
      oktaId: userOktaIdSelector(state),
      skipContentFrontloadRequest: skipContentFrontloadRequestSelector(state),
    };
  };
};

const frontload = async (props: any) => {
  if (props.skipContentFrontloadRequest) return props.resetSkipContentFrontloadRequest();
  if (props.industrySlug) {
    return props.fetchAggsPageContent();
  }
};

const options = {
  onUpdate: true,
  _experimental_updateFunc: (prevProps: any, newProps: any) => {
    return prevProps.industrySlug !== newProps.industrySlug || prevProps.isAuth !== newProps.isAuth;
  },
};

export const PageIndustryAggregationContainer = connect(
  mapStateToProps,
  mapActionCreators
)(frontloadConnect(frontload, options)(PageIndustryAggregation));
